import { PickDeep } from 'type-fest'

import { ALERT_FRAGMENT } from 'features/alerts/api/fragments/alert'
import { alertsFromDTO } from 'features/alerts/api/mappers/alertFromDTO'
import {
  QueryProps,
  QueryVariables,
} from 'features/inventory/ad-unit/components/AdUnitsTable/types/api'
import { AdUnitDTO } from 'features/inventory/api/types/adUnitDTO'
import { DomainAlert } from 'features/inventory/domain/types/DomainAlert'
import { AdUnit } from 'features/inventory/types/adUnit'
import { Domain } from 'features/inventory/types/domain'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { DEFAULT_REFETCH_INTERVAL } from 'providers/tanstack-query/constants'
import { useQuery } from 'providers/tanstack-query/useQuery'
import { gql } from 'providers/tanstack-query/utils'
import { sortToDTO } from './useAdUnits'

export type Alert = Pick<AdUnit, 'id'> & Pick<Domain, 'alerts'>

type Node = PickDeep<AdUnitDTO, 'domain.alerts' | 'id'>
type QueryData = {
  adUnits: {
    nodes: Node[] | null
  }
}

export const useGetAlerts = ({ filters, first, last, sort }: QueryProps): Alert[] => {
  const { workspaceId } = useWorkspaceParam()

  const { data } = useQuery<QueryVariables, QueryData, Alert[]>({
    options: {
      refetchInterval: DEFAULT_REFETCH_INTERVAL,
      select: (data: QueryData): Alert[] =>
        data.adUnits.nodes?.map(
          (adUnit: Node): Alert => ({
            alerts: alertsFromDTO<DomainAlert>(adUnit.domain.alerts),
            id: adUnit.id,
          }),
        ) ?? [],
    },
    query: gql`
      ${ALERT_FRAGMENT}

      query getAlertsForAdUnitsTable(
        $first: Int
        $last: Int
        $sort: AdUnitSortInput
        $terms: [String!]
        $workspaceId: ID!
      ) {
        adUnits(first: $first, last: $last, sort: $sort, terms: $terms, workspaceId: $workspaceId) {
          nodes {
            id
            domain {
              alerts {
                ...AlertFragment
              }
            }
          }
        }
      }
    `,
    variables: {
      first,
      last,
      sort: sortToDTO(sort),
      terms: filters,
      workspaceId,
    },
  })

  return data ?? []
}
